import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Plus } from 'lucide-react';
import type {
  CallAnalysisTagDefinitionChoice,
  CallAnalysisTagDefinitionResponse,
} from '@reach/shared-types/call-analysis-tag-definitions';
import { DialogBackdrop } from '../../../components/ui/dialog-backdrop';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { ColorFieldRow } from './ColorFieldRow';
import { IconPicker } from './IconPicker';
import { TagDefinitionChoiceCard } from './TagDefinitionChoiceCard';
import { applyPendingChoiceEdit, buildChoice, type ChoiceFields } from './tag-definition-choice-utils';
import styles from './TagDefinitionDialog.module.css';

interface TagDefinitionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    type: 'boolean' | 'enum';
    description: string;
    choices?: CallAnalysisTagDefinitionChoice[];
  }) => void;
  isPending: boolean;
  existing?: CallAnalysisTagDefinitionResponse | null;
}

const EMPTY_CHOICE_FIELDS: ChoiceFields = {
  displayName: '',
  tooltipDescription: '',
  color: '',
  textColor: '',
  icon: '',
};

export const TagDefinitionDialog: React.FC<TagDefinitionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isPending,
  existing,
}) => {
  const isEdit = !!existing;
  const titleId = useId();
  const formId = useId();

  const [name, setName] = useState('');
  const [type, setType] = useState<'boolean' | 'enum'>('boolean');
  const [description, setDescription] = useState('');
  const [choices, setChoices] = useState<CallAnalysisTagDefinitionChoice[]>([]);
  const [draftValue, setDraftValue] = useState('');
  const [draftFields, setDraftFields] = useState<ChoiceFields>(EMPTY_CHOICE_FIELDS);

  const [editingChoiceValue, setEditingChoiceValue] = useState<string | null>(null);
  const [editingFields, setEditingFields] = useState<ChoiceFields>(EMPTY_CHOICE_FIELDS);

  const resetEditing = useCallback(() => {
    setEditingChoiceValue(null);
    setEditingFields(EMPTY_CHOICE_FIELDS);
  }, []);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setType(existing.type);
      setDescription(existing.description);
      setChoices(existing.choices ?? []);
    } else {
      setName('');
      setType('boolean');
      setDescription('');
      setChoices([]);
    }
    setDraftValue('');
    setDraftFields(EMPTY_CHOICE_FIELDS);
    resetEditing();
  }, [existing, open, resetEditing]);

  const handleAddChoice = useCallback(() => {
    const trimmed = draftValue.trim();
    if (!trimmed || choices.some(choice => choice.value === trimmed)) {
      return;
    }
    setChoices(prev => [...prev, buildChoice(trimmed, draftFields)]);
    setDraftValue('');
    setDraftFields(EMPTY_CHOICE_FIELDS);
  }, [draftValue, draftFields, choices]);

  const handleRemoveChoice = useCallback(
    (value: string) => {
      setChoices(prev => prev.filter(choice => choice.value !== value));
      if (editingChoiceValue === value) {
        resetEditing();
      }
    },
    [editingChoiceValue, resetEditing],
  );

  const handleStartEditChoice = useCallback((choice: CallAnalysisTagDefinitionChoice) => {
    setEditingChoiceValue(choice.value);
    setEditingFields({
      displayName: choice.displayName ?? '',
      tooltipDescription: choice.tooltipDescription ?? '',
      color: choice.color ?? '',
      textColor: choice.textColor ?? '',
      icon: choice.icon ?? '',
    });
  }, []);

  const handleCancelEditChoice = useCallback(() => {
    resetEditing();
  }, [resetEditing]);

  const handleSaveEditChoice = useCallback(() => {
    if (!editingChoiceValue) return;
    setChoices(prev => applyPendingChoiceEdit(prev, editingChoiceValue, editingFields));
    resetEditing();
  }, [editingChoiceValue, editingFields, resetEditing]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const choicesForSubmit =
        type === 'enum' && editingChoiceValue
          ? applyPendingChoiceEdit(choices, editingChoiceValue, editingFields)
          : choices;
      onSubmit({
        name: name.trim(),
        type,
        description: description.trim(),
        choices: type === 'enum' ? choicesForSubmit : undefined,
      });
    },
    [name, type, description, choices, onSubmit, editingChoiceValue, editingFields],
  );

  const updateDraftField = useCallback(
    <K extends keyof ChoiceFields>(field: K, value: ChoiceFields[K]) => {
      setDraftFields(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const updateEditingField = useCallback(
    <K extends keyof ChoiceFields>(field: K, value: ChoiceFields[K]) => {
      setEditingFields(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const nameValid = /^[a-z][a-z0-9_]*$/.test(name.trim());
  const isSubmitDisabled =
    isPending ||
    !name.trim() ||
    (!isEdit && !nameValid) ||
    !description.trim() ||
    (type === 'enum' && choices.length < 2);

  if (!open) return null;

  return createPortal(
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialogShell} open aria-modal="true" aria-labelledby={titleId}>
        <div className={styles.dialogPanel}>
          <div className={styles.header}>
            <h3 id={titleId} className={styles.title}>
              {isEdit ? 'Edit Tag Definition' : 'Create Tag Definition'}
            </h3>
            <p className={styles.subtitle}>
              {isEdit
                ? 'Update the description or enum choices. Tag name and type cannot change because they identify this tag on calls.'
                : 'Define a new tag that the AI will evaluate on every call.'}
            </p>
          </div>

          <form id={formId} onSubmit={handleSubmit} className={styles.formBody} noValidate>
            <div className={styles.fieldGroup}>
              <Label htmlFor="tag-name">Name</Label>
              <Input
                id="tag-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. scheduling_request"
                disabled={isEdit}
                className={styles.nameInput}
              />
              {!isEdit && name.trim() && !nameValid && (
                <p className={styles.nameError}>
                  Must be lowercase snake_case (letters, digits, underscores)
                </p>
              )}
            </div>

            <fieldset className={styles.typeFieldset}>
              <legend className={styles.typeLegend}>Type</legend>
              <div className={styles.typeOptions}>
                <label className={`${styles.typeLabel} ${isEdit ? styles.typeLabelDisabled : ''}`}>
                  <input
                    type="radio"
                    name="tag-definition-type"
                    value="boolean"
                    checked={type === 'boolean'}
                    onChange={() => {
                      setType('boolean');
                      setChoices([]);
                    }}
                    disabled={isEdit}
                    className={styles.typeRadio}
                  />
                  Boolean
                </label>
                <label className={`${styles.typeLabel} ${isEdit ? styles.typeLabelDisabled : ''}`}>
                  <input
                    type="radio"
                    name="tag-definition-type"
                    value="enum"
                    checked={type === 'enum'}
                    onChange={() => setType('enum')}
                    disabled={isEdit}
                    className={styles.typeRadio}
                  />
                  Enum
                </label>
              </div>
            </fieldset>

            <div className={styles.fieldGroup}>
              <Label htmlFor="tag-description">Description</Label>
              <textarea
                id="tag-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe what the AI should look for in the call..."
                rows={3}
                className={styles.descriptionTextarea}
              />
              <p className={styles.fieldHint}>
                Sent to the AI as the tagging instruction. Stay specific — this drives accuracy.
              </p>
            </div>

            {type === 'enum' && (
              <div className={styles.fieldGroup}>
                <Label>Choices (min 2)</Label>
                <p className={styles.choiceHint}>
                  Value is stored on the call and sent to the model. Display name, tooltip, background
                  color, text color, and icon are for dashboard display only. Use the pencil to edit
                  styling; Save Changes includes open edits even if you do not click Save choice first.
                </p>
                <div className={styles.choicesList}>
                  {choices.map((choice, index) => {
                    const isEditingThis = editingChoiceValue === choice.value;
                    const previewChoice = isEditingThis
                      ? buildChoice(choice.value, editingFields)
                      : choice;

                    return (
                      <TagDefinitionChoiceCard
                        key={`${choice.value}-${index}`}
                        choice={choice}
                        previewChoice={previewChoice}
                        isEditing={isEditingThis}
                        onStartEdit={() => handleStartEditChoice(choice)}
                        onRemove={() => handleRemoveChoice(choice.value)}
                        editInputIds={{
                          displayName: `edit-display-name-${choice.value}`,
                          tooltipDescription: `edit-tooltip-${choice.value}`,
                          background: `edit-bg-${choice.value}`,
                          textColor: `edit-text-${choice.value}`,
                          icon: `edit-icon-${choice.value}`,
                        }}
                        editFields={editingFields}
                        onEditFieldChange={updateEditingField}
                        onCancelEdit={handleCancelEditChoice}
                        onSaveEdit={handleSaveEditChoice}
                      />
                    );
                  })}
                </div>
                <div className={styles.draftChoice}>
                  <div className={styles.draftChoiceRow}>
                    <Label htmlFor="draft-value" className={styles.srOnly}>
                      Choice value
                    </Label>
                    <Input
                      id="draft-value"
                      value={draftValue}
                      onChange={e => setDraftValue(e.target.value)}
                      placeholder="Value (e.g. sales)"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddChoice();
                        }
                      }}
                      className={styles.draftValueInput}
                    />
                  </div>
                  <div className={styles.draftChoiceRow}>
                    <Label htmlFor="draft-display-name">Display name (optional)</Label>
                    <Input
                      id="draft-display-name"
                      value={draftFields.displayName}
                      onChange={e => updateDraftField('displayName', e.target.value)}
                      placeholder="e.g. New Lead"
                      maxLength={255}
                    />
                  </div>
                  <div className={styles.draftChoiceRow}>
                    <Label htmlFor="draft-tooltip-description">Tooltip description (optional)</Label>
                    <textarea
                      id="draft-tooltip-description"
                      value={draftFields.tooltipDescription}
                      onChange={e => updateDraftField('tooltipDescription', e.target.value)}
                      placeholder="Plain-English explanation shown on hover..."
                      rows={2}
                      maxLength={1024}
                      className={styles.descriptionTextarea}
                    />
                  </div>
                  <ColorFieldRow
                    label="Background"
                    colorValue={draftFields.color}
                    onColorValueChange={v => updateDraftField('color', v)}
                    inputId="draft-color"
                  />
                  <ColorFieldRow
                    label="Text"
                    colorValue={draftFields.textColor}
                    onColorValueChange={v => updateDraftField('textColor', v)}
                    inputId="draft-text-color"
                  />
                  <IconPicker
                    value={draftFields.icon}
                    onChange={v => updateDraftField('icon', v)}
                    id="draft-icon-picker"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={handleAddChoice}>
                    <Plus />
                    Add choice
                  </Button>
                </div>
              </div>
            )}
          </form>

          <div className={styles.footer}>
            <Button variant="outline" size="sm" type="button" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button form={formId} size="sm" type="submit" disabled={isSubmitDisabled}>
              {isPending ? (
                <>
                  <Loader2 />
                  {isEdit ? 'Saving...' : 'Creating...'}
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Create Tag'
              )}
            </Button>
          </div>
        </div>
      </dialog>
    </>,
    document.body,
  );
};
